import * as THREE from 'three';
import { clamp } from '@/core/rng';

/**
 * The people of a band, standing where the simulation actually put them.
 *
 * A band is not one creature doing one thing — it splits into parties, and
 * each party has its own patch of ground, its own destination and its own
 * work. Until now every band rendered as a single clump at its centre of
 * mass, which meant the most legible thing the simulation does was invisible:
 * hunters an hour out along a ridge and foragers on the grass averaged into
 * one crowd standing between them, doing nothing in particular.
 *
 * So the unit of rendering is the party, not the band. Each party lays its
 * own members out around its own anchor in a shape that says what they are
 * doing — a file strung out along the walk, a scatter across foraging ground,
 * a line facing the herd, a ring around the fire — and the people in it are
 * the band's actual named people, so a person keeps their build and their
 * garb for their whole life and across every party they ever walk with.
 *
 * Measured against a 180-day god-free run (`tools/parties.ts`): two to four
 * parties at a time, twelve people in the median party, 213 m between two of
 * them at the median and up to 2 km, and — the number that decides the
 * layout — hunting and foraging parties are *walking* 90% of the hours they
 * exist. The road is where these lives are mostly lived, so the walking
 * formation is the one that has to read.
 *
 * This module reads plain data and owns no simulation.
 */

/** One person, as far as the renderer needs to care: an identity and a body. */
export interface PersonView {
  id: string;
  /** The party they walk with, matched against `PartyView.id`. */
  partyId?: string;
  /** 0..1 general condition; the frail stand a little smaller. */
  vitality?: number;
}

export interface PartyView {
  id: string;
  /** What this party is out for — the formation reads from it. */
  activity: string;
  /** Souls in the party. Used only when the band has no roster to draw on. */
  size: number;
  /** Party anchor; y is the fallback ground when no groundAt is supplied. */
  x: number; z: number; y: number;
  /** Unit heading toward the destination, or outward from home when still. */
  hx: number; hz: number;
  /** True while the party is genuinely on the move rather than working. */
  walking: boolean;
}

export interface BandView {
  id: string;
  parties: PartyView[];
  /** The band's roster. Empty is legal — the parties are then filled by size. */
  people: PersonView[];
}

/**
 * Total figures on screen. One band of twenty-five needs twenty-five, and the
 * cap only exists so that a valley of bands cannot cost an unbounded number
 * of matrix updates; when it binds, parties are thinned evenly rather than
 * truncated, so no party ever vanishes because another one was drawn first.
 */
const CAP = 384;

/**
 * Diorama scale. A real 1.9 m person is two or three pixels from any distance
 * that shows a hillside, so the story of the game would be invisible at the
 * range the game is played. Figures stand a little over three metres — the
 * same compression a model railway uses on its people, and the same choice
 * Black & White made: humans slightly oversized so you can see them being
 * human. Nothing in the simulation knows or cares.
 */
const FIGURE_SCALE = 1.7;

/**
 * The body, and why it is shaped the way it is.
 *
 * `tools/figures.ts` and `tools/gait.ts` priced this rather than guessing it.
 * At the 340 m the game opens at a figure is 14 px tall, and of nine candidate
 * features only four clear the three-pixel signal threshold this codebase
 * already uses for litter picks (`main.ts`' `pxPerMetre`, `tools/settlement.ts`'
 * rule):
 *
 * | feature | px at 340 m | still reads at |
 * |---|---:|---|
 * | leg length | 6.6 | **700 m** |
 * | stride reach | 4.2 | 340 m |
 * | shoulder width | 3.7 | 340 m |
 * | head diameter | 2.0 | 150 m |
 * | stance gap | 1.6 | 150 m |
 * | limb thickness | 0.8 | 80 m |
 * | vertical bob | 0.5 | 40 m |
 *
 * The figure this replaced was a six-sided cone with a ball on top, and the
 * measurement says it was **upside down**: it was widest at the feet and
 * tapered to a point exactly where the shoulders — the third-most legible
 * feature on the body — should have been, and it had no legs at all, which is
 * the one feature still resolving at seven hundred metres.
 *
 * So: broad shoulders, a narrowing torso, two legs, two arms, and a stride.
 * Nothing is spent on limb thickness or on the head beyond a blob, because
 * neither survives past the range where you are already close enough to be
 * looking at one person rather than at a band.
 *
 * 76 triangles against the old 38 — at the 384 cap that is 29 k triangles in
 * the same one draw call, against the ~4.7 M the frame already carries.
 */
const H = 2.31;                        // total height; unchanged, so every
                                       // per-person scale downstream still holds
const HIP = 0.470 * H;                 // leg length, the feature that reads furthest
const SHOULDER = 0.820 * H;
const HALF_SHOULDER = 0.130 * H;       // half of the 0.26 H shoulder width
const STANCE = 0.0575 * H;             // half the stance gap, legs either side of centre
const ARM_TOP = 0.805 * H, ARM_LEN = 0.355 * H;
const HALF_ARM = 0.124 * H;

/**
 * A limb, and how it moves. `swing` is the gait multiplier — legs at ±1, arms
 * counter-swinging their own side at ∓0.85, the torso and head at 0 — and
 * `joint` is the point in figure space it rotates about. The vertex shader
 * does the rotation, so a walking crowd is still one instanced draw.
 */
interface Part { geo: THREE.BufferGeometry; swing: number; joint: [number, number, number] }

/** Merge the parts into one non-indexed body, carrying swing and joint per vertex. */
function figureGeometry(): THREE.BufferGeometry {
  const parts: Part[] = [
    // torso: widest at the shoulder, narrowing to the hip, and flattened
    // front-to-back because a chest is wider than it is deep
    { swing: 0, joint: [0, 0, 0],
      geo: new THREE.CylinderGeometry(HALF_SHOULDER, 0.080 * H, SHOULDER - HIP, 6, 1)
        .scale(1, 1, 0.62).translate(0, (SHOULDER + HIP) / 2, 0) },
    // the head is a blob on purpose: 2 px at the altitude the game opens at
    { swing: 0, joint: [0, 0, 0],
      geo: new THREE.IcosahedronGeometry(0.080 * H, 0).translate(0, 0.918 * H, 0) },
  ];
  // legs, then arms — open-ended, because the top of a leg is inside the torso
  // and the bottom of one is against the ground disc
  for (const side of [-1, 1]) {
    parts.push({
      swing: side, joint: [side * STANCE, HIP, 0],
      geo: new THREE.CylinderGeometry(0.033 * H, 0.024 * H, HIP, 4, 1, true)
        .translate(side * STANCE, HIP / 2, 0),
    });
    parts.push({
      swing: -side * 0.85, joint: [side * HALF_ARM, ARM_TOP, 0],
      geo: new THREE.CylinderGeometry(0.024 * H, 0.017 * H, ARM_LEN, 4, 1, true)
        .translate(side * HALF_ARM, ARM_TOP - ARM_LEN / 2, 0),
    });
  }

  // cylinders come indexed and icosahedra do not, so only flatten what needs it
  const flat = parts.map((p) => ({ ...p, geo: p.geo.getIndex() ? p.geo.toNonIndexed() : p.geo }));
  const total = flat.reduce((s, p) => s + p.geo.attributes.position.count, 0);
  const pos = new Float32Array(total * 3), nor = new Float32Array(total * 3);
  const swing = new Float32Array(total), joint = new Float32Array(total * 3);
  let v = 0;
  for (const p of flat) {
    const a = p.geo.attributes.position.array as Float32Array;
    const b = p.geo.attributes.normal.array as Float32Array;
    pos.set(a, v * 3); nor.set(b, v * 3);
    for (let i = 0; i < p.geo.attributes.position.count; i++) {
      swing[v + i] = p.swing;
      joint[(v + i) * 3] = p.joint[0];
      joint[(v + i) * 3 + 1] = p.joint[1];
      joint[(v + i) * 3 + 2] = p.joint[2];
    }
    v += p.geo.attributes.position.count;
  }
  new Set([...parts, ...flat].map((p) => p.geo)).forEach((g) => g.dispose());

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.setAttribute('swing', new THREE.BufferAttribute(swing, 1));
  geo.setAttribute('joint', new THREE.BufferAttribute(joint, 3));
  return geo;
}

/** FNV-1a over the whole id — 'band-1' and 'band-2' must not share a layout. */
function idSeed(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** A stable 0..1 from a person's seed and a channel — their own dice, forever. */
const roll = (seed: number, channel: number): number => {
  let h = Math.imul(seed ^ Math.imul(channel + 0x9e37, 0x85eb), 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h ^ (h >>> 13), 0x27d4eb2f);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

/**
 * How a party stands when it is working, by what the work is. `spread` is the
 * radius its people occupy; `ahead` biases them toward the heading (a hunting
 * line faces the herd, a scatter does not); `inward` turns them to face the
 * anchor (people work *on* a shelter frame); `seated` puts them on the ground.
 *
 * The radii are metres of real ground. A 34 m forage scatter is about an
 * eighth of the screen at the 340 m the game opens at — spread enough to read
 * as ground being worked, tight enough to still read as one group.
 */
const FORM: Record<string, { near: number; far: number; ahead: number; inward: boolean; seated: boolean }> = {
  camp:        { near: 3.0, far: 12.0, ahead: 0.0, inward: false, seated: false },
  idle:        { near: 3.0, far: 10.0, ahead: 0.0, inward: false, seated: false },
  rest:        { near: 2.0, far: 8.0,  ahead: 0.0, inward: false, seated: true },
  build:       { near: 1.6, far: 6.0,  ahead: 0.0, inward: true,  seated: false },
  forage:      { near: 8.0, far: 34.0, ahead: 0.0, inward: false, seated: false },
  gather_wood: { near: 6.0, far: 26.0, ahead: 0.0, inward: false, seated: false },
  seek_water:  { near: 3.0, far: 14.0, ahead: 0.0, inward: false, seated: false },
  explore:     { near: 6.0, far: 26.0, ahead: 0.6, inward: false, seated: false },
  // people who walked to a sign stand looking at the place, not at each other
  investigate: { near: 4.0, far: 18.0, ahead: 0.5, inward: true, seated: false },
  // at the stone people gather close and face it
  venerate:    { near: 3.0, far: 11.0, ahead: 0.0, inward: true, seated: false },
  hunt:        { near: 10.0, far: 32.0, ahead: 0.8, inward: false, seated: false },
  travel:      { near: 3.0, far: 12.0, ahead: 0.0, inward: false, seated: false },
  flee:        { near: 3.0, far: 10.0, ahead: 0.0, inward: false, seated: false },
};
const DEFAULT_FORM = FORM.camp;

export interface Mortals {
  group: THREE.Group;
  /**
   * `groundAt` resolves terrain height at each figure's final position — a
   * party spreads tens of metres and its parties spread kilometres, so on a
   * slope an anchor's y would float or bury half of them. Omit it and the
   * party's own y is used.
   */
  update(tSec: number, bands: BandView[], camR: number,
         groundAt?: (x: number, z: number) => number): void;
  /**
   * Watch one particular life. A quiet ring marks where they are standing,
   * and they are guaranteed a body even when the cap is thinning their party
   * — you cannot follow somebody the crowd budget dropped.
   */
  follow(personId: string | null): void;
  /**
   * Where the followed figure ended up this frame, or null if they were not
   * drawn (too far, or dead). The camera reads this; the simulation never does.
   */
  positionOf(personId: string): { x: number; y: number; z: number } | null;
  /**
   * Who the pointer just hit. The figures are one instanced mesh, so a
   * raycast comes back with an instance number and nothing else; this is the
   * only place that knows which life was drawn there this frame. Reading it
   * changes nothing — it is the inverse of the bookkeeping `positionOf`
   * already keeps.
   */
  personAt(instanceId: number): string | null;
  /**
   * Whoever is standing nearest a point, among the figures actually drawn
   * this frame. The pointer resolves people this way rather than by ray: one
   * instanced mesh holds every figure on the island, so its bounding sphere
   * is the island and a raycast against it tests every body in the world.
   */
  nearestTo(x: number, z: number, within: number):
    { id: string; x: number; y: number; z: number } | null;
}

export function createMortals(): Mortals {
  const geo = figureGeometry();
  // Per-instance gait: phase, rate, amplitude. Held on the geometry as an
  // instanced attribute so the walk costs one buffer upload a frame and no
  // extra draw — three 0.160 has no instanced skinning, and at 14 px a
  // skeleton would be paying for joints nobody can resolve anyway.
  const gaitAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);
  gaitAttr.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aGait', gaitAttr);
  const gait = gaitAttr.array as Float32Array;

  const material = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0 });
  const gaitTime = { value: 0 };
  // The walk itself. Each limb turns about its own joint in figure space,
  // before the instance matrix places and scales it — so the stride is a
  // fixed share of the body however tall this particular person is, and a
  // party of twelve walks in twelve different phases off their own seeds.
  material.onBeforeCompile = (sh) => {
    sh.uniforms.uGaitTime = gaitTime;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        uniform float uGaitTime;
        attribute float swing;
        attribute vec3 joint;
        attribute vec3 aGait;
        // aGait = (phase, rate, amplitude in radians)
        float gaitAngle() {
          return sin(uGaitTime * aGait.y + aGait.x) * aGait.z * swing;
        }
        vec3 gaitRot(vec3 p, vec3 j, float a) {
          vec3 r = p - j;
          float c = cos(a), s = sin(a);
          return j + vec3(r.x, c * r.y - s * r.z, s * r.y + c * r.z);
        }`)
      // normals first: three transforms objectNormal by the instance matrix
      // downstream, so the limb has to be turned here or a swinging leg lights
      // as though it were still hanging straight
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        if (abs(swing) > 0.001) objectNormal = gaitRot(objectNormal, vec3(0.0), gaitAngle());`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        if (abs(swing) > 0.001) transformed = gaitRot(transformed, joint, gaitAngle());`);
  };

  const mesh = new THREE.InstancedMesh(geo, material, CAP);
  mesh.name = 'figures';
  mesh.frustumCulled = false;
  // A person with no shadow stands on the ground the way a sticker does. The
  // soft disc below is a ground TIE and has always been there precisely
  // because there was no real shadow to do the job; now there is one, and the
  // disc keeps earning its keep at ranges where a shadow texel is bigger than
  // a body.
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3);

  // the ground ties: one soft dark disc under each figure
  const discs = new THREE.InstancedMesh(
    new THREE.CircleGeometry(0.72, 20).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({
      color: 0x0b0f0a, transparent: true, opacity: 0.3, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    }), CAP);
  discs.name = 'contacts';
  discs.frustumCulled = false;

  // the attention ring: where the god is looking, drawn for the player alone.
  // It grows with the pull-back so a followed life stays findable from grove
  // scale, and it never becomes a selection box — this is watching, not
  // owning, and there is nothing to click.
  // A hairline at half opacity measured about one pixel of stroke at 190 m and
  // could not be found on the ground at all: this is a broad band of light,
  // not an outline.
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.62, 1, 48).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({
      color: 0xffd257, transparent: true, opacity: 0.7, depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
    }));
  ring.name = 'attention';
  ring.frustumCulled = false;
  ring.visible = false;

  const group = new THREE.Group();
  group.add(mesh, discs, ring);

  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler();
  const lean = new THREE.Quaternion(), leanAxis = new THREE.Vector3();
  const pos = new THREE.Vector3(), scl = new THREE.Vector3();
  const col = new THREE.Color();
  // deep earth tones, one brick accent — read against green, not into it
  const GARB = [[0.45, 0.36, 0.25], [0.33, 0.28, 0.21], [0.56, 0.45, 0.30],
                [0.58, 0.26, 0.18], [0.29, 0.26, 0.24]];

  // scratch, reused every frame: who walks with which party
  const roster = new Map<string, PersonView[]>();
  const spare: PersonView[][] = [];
  /** Stand-ins for a band whose roster has not caught up with its number. */
  const anon: PersonView[] = [];

  /** Where each figure ended up, stamped with the frame that put it there. */
  const placed = new Map<string, { x: number; y: number; z: number; stamp: number }>();
  /** …and who stands at each instance, for the pointer to read back. */
  const drawn: string[] = [];
  let stamp = 0;
  let followed: string | null = null;

  return {
    group,
    follow(personId) { followed = personId; },
    positionOf(personId) {
      const p = placed.get(personId);
      return p && p.stamp === stamp ? { x: p.x, y: p.y, z: p.z } : null;
    },
    personAt(instanceId) {
      return instanceId >= 0 && instanceId < mesh.count ? drawn[instanceId] ?? null : null;
    },
    nearestTo(x, z, within) {
      let best: { id: string; x: number; y: number; z: number } | null = null;
      let bestD = within;
      for (const [id, p] of placed) {
        if (p.stamp !== stamp) continue;          // not drawn this frame
        const d = Math.hypot(p.x - x, p.z - z);
        if (d < bestD) { bestD = d; best = { id, x: p.x, y: p.y, z: p.z }; }
      }
      return best;
    },
    update(tSec, bands, camR, groundAt) {
      stamp++;
      gaitTime.value = tSec;
      ring.visible = false;
      if (placed.size > 4096) placed.clear();   // a long game must not hoard the dead
      // people are valley-scale life; from god height they are settlement, smoke, paths
      const visible = camR < 5000 && bands.length > 0;
      group.visible = visible;
      if (!visible) { mesh.count = 0; discs.count = 0; return; }

      // ---- who is where: bucket every band's roster by party ----
      for (const list of roster.values()) { list.length = 0; spare.push(list); }
      roster.clear();
      let wanted = 0, partyCount = 0;
      for (const band of bands) {
        for (const p of band.parties) {
          const list = spare.pop() ?? [];
          roster.set(p.id, list);
          partyCount++;
        }
        for (const person of band.people) {
          const list = person.partyId ? roster.get(person.partyId) : undefined;
          if (list) list.push(person);
        }
        for (const p of band.parties) {
          const list = roster.get(p.id)!;
          // a roster that has not caught up with the number still shows bodies:
          // the party's size is the truth, and stand-ins carry it
          if (list.length === 0) {
            for (let i = 0; i < Math.min(16, Math.max(1, Math.round(p.size))); i++) {
              const k = wanted + i;
              if (!anon[k]) anon[k] = { id: '' };
              anon[k].id = `${p.id}#${i}`;
              list.push(anon[k]);
            }
          }
          wanted += list.length;
        }
      }
      // When the cap binds, one body is set aside for every party before any
      // party gets a second, and the rest of the budget is shared out in
      // proportion. So a valley of bands thins evenly and every party keeps
      // somebody standing in it — nothing vanishes because it was drawn last.
      // The total is then at most partyCount + (CAP − partyCount) = CAP.
      const share = wanted > CAP && wanted > partyCount
        ? Math.max(0, CAP - partyCount) / (wanted - partyCount)
        : 1;

      let n = 0;
      for (const band of bands) {
        for (const party of band.parties) {
          const members = roster.get(party.id)!;
          if (members.length === 0) continue;
          const shown = share >= 1
            ? members.length
            : 1 + Math.floor((members.length - 1) * share);
          // an even stride, so a thinned party is still spread across its shape
          const stride = members.length / shown;
          const form = FORM[party.activity] ?? DEFAULT_FORM;
          const fleeing = party.activity === 'flee';
          // the walking file lengthens with the party: twelve people make a
          // column about thirty metres long, which is what a band on the move
          // looks like from three hundred metres up
          const fileLen = Math.min(46, 7 + shown * 2.1) * (fleeing ? 0.62 : 1);
          const hx = party.hx, hz = party.hz;
          // a thinned party must not be allowed to drop the one life the
          // player is watching: they take the slot nearest their own place in
          // the file, so following somebody does not reshuffle the column
          let heldSlot = -1, held: PersonView | undefined;
          if (followed !== null && shown < members.length) {
            const i0 = members.findIndex((p) => p.id === followed);
            if (i0 >= 0) {
              heldSlot = Math.min(shown - 1, Math.round(i0 / stride));
              held = members[i0];
            }
          }

          for (let j = 0; j < shown && n < CAP; j++, n++) {
            const person = j === heldSlot && held
              ? held
              : members[Math.min(members.length - 1, Math.floor(j * stride))];
            const seed = idSeed(person.id);
            const h1 = roll(seed, 1), h2 = roll(seed, 2), h3 = roll(seed, 3);
            const h4 = roll(seed, 4);
            // the file's ordering is the party's, so the column is evenly
            // spaced whoever happens to be walking in it today
            const rank = shown > 1 ? j / (shown - 1) : 0;

            let dx: number, dz: number;
            let yaw: number;
            let seated = false;
            if (party.walking) {
              // strung out behind the anchor, braided rather than ruled
              const back = rank * fileLen;
              const side = (h1 - 0.5) * (fleeing ? 9 : 6.5)
                         + Math.sin(rank * 5.3 + h2 * 6.3) * 2.2;
              dx = -hx * back - hz * side;
              dz = -hz * back + hx * side;
              yaw = Math.atan2(hx, hz);
            } else {
              const a = h1 * Math.PI * 2;
              const r = form.near + h2 * (form.far - form.near);
              dx = Math.cos(a) * r + hx * form.ahead * r;
              dz = Math.sin(a) * r + hz * form.ahead * r;
              yaw = form.inward ? Math.atan2(-dx, -dz) : h3 * Math.PI * 2;
              seated = form.seated;
            }

            // a slow individual sway so a camp never looks like statuary
            const idleRate = 0.5 + h3 * 0.4;
            const sway = Math.sin(tSec * idleRate + h1 * 9) * 0.6;
            const fx = party.x + dx + sway, fz = party.z + dz;
            const gy = groundAt ? groundAt(fx, fz) : party.y;
            pos.set(fx, gy, fz);
            q.setFromEuler(e.set(0, yaw, 0, 'YXZ'));
            // The gait, on one clock the legs and the body both read. Amplitude
            // is measured rather than chosen: `tools/gait.ts` puts stride reach
            // at 0.30 of body height, which across a 0.47 H leg is
            // asin(0.15 / 0.47) ≈ 0.33 rad of swing either side of vertical.
            // Standing keeps a small weight shift at the same rate as the sway,
            // so a figure at the fire moves as one thing rather than sliding
            // sideways on frozen legs.
            const rate = party.walking ? (fleeing ? 4.4 : 3.1) : idleRate;
            gait[n * 3] = h1 * 9;
            gait[n * 3 + 1] = rate;
            gait[n * 3 + 2] = party.walking ? (fleeing ? 0.45 : 0.33) : 0.05;
            if (party.walking) {
              // the walk: face the heading, lean into it, land each step.
              // The rise is `cos` against the legs' `sin`, so the body is
              // tallest as the feet pass and lowest at full stride — which is
              // what walking does, and was inverted while the legs were a cone.
              lean.setFromAxisAngle(leanAxis.set(hz, 0, -hx), fleeing ? 0.26 : 0.16);
              q.premultiply(lean);
              pos.y += Math.abs(Math.cos(tSec * rate + h1 * 9)) * 0.14;
            }
            // build differs by a hand's breadth, and hardship shows in it
            const frail = 0.86 + 0.14 * clamp(person.vitality ?? 1, 0, 1);
            const height = (0.92 + h3 * 0.22) * FIGURE_SCALE * frail;
            // sitting is half a body: at the fire, people are down on the ground
            const stand = seated ? height * 0.62 : height;
            scl.set(height, stand, height);
            m.compose(pos, q, scl);
            mesh.setMatrixAt(n, m);
            const g = GARB[Math.floor(h4 * GARB.length) % GARB.length];
            mesh.setColorAt(n, col.setRGB(
              g[0] * (0.85 + h1 * 0.3), g[1] * (0.85 + h2 * 0.3), g[2] * (0.85 + h3 * 0.3)));
            // the disc hugs the ground under the figure, whatever the bob does
            m.compose(pos.set(fx, gy + 0.05, fz), q.identity(), scl.set(height, 1, height));
            discs.setMatrixAt(n, m);

            let spot = placed.get(person.id);
            if (!spot) placed.set(person.id, spot = { x: 0, y: 0, z: 0, stamp: 0 });
            spot.x = fx; spot.y = gy; spot.z = fz; spot.stamp = stamp;
            drawn[n] = person.id;
            if (person.id === followed) {
              // wide enough to find from grove scale, and breathing a little
              // so a still figure at the fire is still visibly attended to
              const rad = clamp(camR * 0.02, 2.2, 24) * (1 + Math.sin(tSec * 1.6) * 0.05);
              ring.position.set(fx, gy + 0.09, fz);
              ring.scale.set(rad, 1, rad);
              ring.visible = true;
            }
          }
        }
      }
      mesh.count = n;
      discs.count = n;
      mesh.instanceMatrix.needsUpdate = true;
      discs.instanceMatrix.needsUpdate = true;
      gaitAttr.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
  };
}
